/* =========================================================================
   BUMPY VICE — pot de produit en 3D
   Three.js est chargé depuis cdnjs uniquement quand la section entre dans
   le champ de vision, et seulement si WebGL est disponible et que le
   visiteur n'a pas demandé moins d'animations.
   Sinon : l'image produit statique reste affichée (aucun octet gaspillé).
   ========================================================================= */
(function () {
  'use strict';

  var THREE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
  var loading = null;

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) {
      return false;
    }
  }

  function loadThree() {
    if (window.THREE) return Promise.resolve();
    if (loading) return loading;

    loading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = THREE_URL;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return loading;
  }

  function initStage(stage) {
    if (stage.dataset.booted === 'true') return;
    stage.dataset.booted = 'true';

    var canvas = stage.querySelector('canvas');
    if (!canvas) return;

    var THREE = window.THREE;
    var labelSrc = stage.dataset.texture;
    var neon = stage.dataset.neon || '#39FF14';
    var pink = stage.dataset.pink || '#FF2E88';
    var speed = parseFloat(stage.dataset.speed || '0.28');

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputEncoding = THREE.sRGBEncoding;

    var scene = new THREE.Scene();

    var camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0.35, 5.4);

    /* --- Lumières néon --------------------------------------------------- */
    scene.add(new THREE.AmbientLight(0xffffff, 0.22));

    var keyLight = new THREE.PointLight(new THREE.Color(neon), 2.6, 22);
    keyLight.position.set(-3.2, 2.2, 3.4);
    scene.add(keyLight);

    var fillLight = new THREE.PointLight(new THREE.Color(pink), 2.2, 22);
    fillLight.position.set(3.4, -0.6, 2.6);
    scene.add(fillLight);

    var rimLight = new THREE.DirectionalLight(0x00e5ff, 0.7);
    rimLight.position.set(0.5, 1.5, -3);
    scene.add(rimLight);

    /* --- Le pot ---------------------------------------------------------- */
    var group = new THREE.Group();
    scene.add(group);

    var plastic = new THREE.MeshStandardMaterial({ color: 0x0c0c0e, roughness: 0.42, metalness: 0.35 });

    var labelMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.55, metalness: 0.15 });

    if (labelSrc) {
      new THREE.TextureLoader().load(
        labelSrc,
        function (tex) {
          tex.encoding = THREE.sRGBEncoding;
          tex.wrapS = THREE.RepeatWrapping;
          tex.wrapT = THREE.ClampToEdgeWrapping;
          tex.repeat.set(1, 1);
          tex.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
          labelMaterial.map = tex;
          labelMaterial.needsUpdate = true;
        },
        undefined,
        function () { /* texture indisponible : le pot reste noir mat */ }
      );
    }

    // Corps : l'ordre des matériaux suit les groupes de CylinderGeometry
    // (0 = paroi, 1 = dessus, 2 = dessous).
    var body = new THREE.Mesh(
      new THREE.CylinderGeometry(1, 1, 1.9, 64, 1, false),
      [labelMaterial, plastic, plastic]
    );
    group.add(body);

    var lid = new THREE.Mesh(new THREE.CylinderGeometry(1.06, 1.06, 0.36, 64), plastic);
    lid.position.y = 1.13;
    group.add(lid);

    var lidTop = new THREE.Mesh(new THREE.CylinderGeometry(0.98, 1.06, 0.1, 64), plastic);
    lidTop.position.y = 1.34;
    group.add(lidTop);

    var base = new THREE.Mesh(new THREE.CylinderGeometry(1.02, 1.02, 0.12, 64), plastic);
    base.position.y = -0.98;
    group.add(base);

    /* --- Asphalte mouillé : capte les néons ------------------------------ */
    var floor = new THREE.Mesh(
      new THREE.CircleGeometry(6, 48),
      new THREE.MeshStandardMaterial({ color: 0x07070a, roughness: 0.18, metalness: 0.9, transparent: true, opacity: 0.85 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.08;
    scene.add(floor);

    group.rotation.z = 0.06;

    /* --- Interaction : souris + scroll ----------------------------------- */
    var pointer = { x: 0, y: 0 };
    var target = { x: 0, y: 0 };
    var scrollTilt = 0;

    stage.addEventListener('pointermove', function (e) {
      var r = stage.getBoundingClientRect();
      target.x = ((e.clientX - r.left) / r.width - 0.5) * 2;
      target.y = ((e.clientY - r.top) / r.height - 0.5) * 2;
    });
    stage.addEventListener('pointerleave', function () { target.x = 0; target.y = 0; });

    function onScroll() {
      var r = stage.getBoundingClientRect();
      var center = r.top + r.height / 2;
      scrollTilt = (center / window.innerHeight - 0.5) * -0.5;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* --- Dimensionnement -------------------------------------------------- */
    function resize() {
      var w = stage.clientWidth || 1;
      var h = stage.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.fov = w < 520 ? 44 : 38;
      camera.updateProjectionMatrix();
    }
    resize();

    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(stage);
    else window.addEventListener('resize', resize);

    /* --- Boucle de rendu, mise en pause hors écran ------------------------ */
    var visible = true;
    var raf = null;
    var last = performance.now();

    function frame(now) {
      raf = requestAnimationFrame(frame);
      var dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      pointer.x += (target.x - pointer.x) * 0.06;
      pointer.y += (target.y - pointer.y) * 0.06;

      group.rotation.y += speed * dt + pointer.x * 0.004;
      group.rotation.x = pointer.y * 0.22 + scrollTilt;
      group.position.y = Math.sin(now / 1400) * 0.05;

      keyLight.position.x = -3.2 + pointer.x * 0.8;
      fillLight.position.x = 3.4 + pointer.x * 0.8;

      renderer.render(scene, camera);
    }

    function play() { if (!raf) { last = performance.now(); raf = requestAnimationFrame(frame); } }
    function pause() { if (raf) { cancelAnimationFrame(raf); raf = null; } }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        visible ? play() : pause();
      }, { threshold: 0.05 }).observe(stage);
    } else {
      play();
    }

    document.addEventListener('visibilitychange', function () {
      document.hidden || !visible ? pause() : play();
    });

    stage.dataset['3d'] = 'on';
    play();
  }

  /* --- Détection des sections 3D ------------------------------------------ */
  function boot() {
    var stages = Array.prototype.slice.call(document.querySelectorAll('[data-3d-stage]'));
    if (!stages.length) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !hasWebGL()) return; // le repli statique reste en place

    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        var stage = entry.target;
        loadThree()
          .then(function () { initStage(stage); })
          .catch(function () { /* cdnjs injoignable : image statique conservée */ });
      });
    }, { rootMargin: '250px 0px' });

    stages.forEach(function (s) { observer.observe(s); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', boot);
})();
